import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, BookOpen, GraduationCap, CreditCard, User, Calendar, FileCheck, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { supabase } from "../../lib/supabase";

export default function StudentDashboard() {
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      
      // 1. Get authenticated user session info
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error("Authentication session failed.");
      setUserProfile(user);

      // 2. Fetch student details from meta table
      const { data: meta, error: metaError } = await supabase
        .from("student_meta")
        .select("*")
        .eq("id", user.id)
        .single();

      if (metaError && metaError.code !== "PGRST116") {
        throw metaError;
      }

      if (meta) {
        setStudentData(meta);
      }
    } catch (error) {
      toast.error(error.message || "Error loading student profile data.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Logout failed.");
    } else {
      toast.success("Logged out successfully!");
      navigate("/login");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF7] flex flex-col items-center justify-center gap-3">
        <Loader2 className="animate-spin text-orange-500" size={40} />
        <p className="text-slate-500 font-medium text-sm">Loading your student profile...</p>
      </div>
    );
  }

  // Calculate remaining fee amount safely
  const remainingFee = studentData ? studentData.total_fee - studentData.paid_fee : 0;

  return (
    <div className="min-h-screen bg-[#FFFBF7] font-sans text-slate-800">
      {/* --- Dashboard Top Header Navbar --- */}
      <nav className="bg-white border-b border-orange-50/60 sticky top-0 z-30 px-6 py-4 shadow-sm shadow-orange-900/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-orange-500 to-amber-400 rounded-xl flex items-center justify-center text-white font-black shadow-md shadow-orange-200">
              <GraduationCap size={22} />
            </div>
            <div>
              <h1 className="font-black text-lg tracking-tight text-slate-900">Divya Computer Portal</h1>
              <p className="text-xs font-bold text-orange-500 uppercase tracking-widest">Student Panel</p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-red-500 transition-colors bg-slate-50 hover:bg-red-50 px-4 py-2.5 rounded-xl border border-transparent hover:border-red-100"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </nav>

      {/* --- Main Section Space Container --- */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        
        {/* Welcome Block Summary Banner */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-[2rem] p-8 text-white mb-10 shadow-xl shadow-slate-900/10 relative overflow-hidden">
          <div className="absolute right-[-5%] top-[-20%] w-64 h-64 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <p className="text-orange-400 text-xs font-black uppercase tracking-widest mb-1">Welcome back</p>
          <h2 className="text-3xl font-black tracking-tight">{userProfile?.email?.split('@')[0]}</h2>
          <p className="text-slate-400 text-sm mt-1 max-w-md">Track your fee structural history, download credentials, and view course details from one central terminal panel.</p>
        </div>

        {!studentData ? (
          /* Empty State Notice UI Box */
          <div className="bg-white rounded-[2rem] p-12 text-center border border-dashed border-slate-200 max-w-xl mx-auto shadow-sm">
            <User className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-bold text-slate-900">Profile Awaiting Allocation</h3>
            <p className="text-slate-500 text-sm mt-2">Your system registration is valid, but an administrator hasn't assigned course credentials or fees to your profile yet. Please connect with management to sync details.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* LEFT 2 COLS: Core Profile Details & Financials */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Card Row 1: Profile Properties */}
              <div className="bg-white p-8 rounded-[2rem] shadow-xl shadow-orange-900/5 border border-orange-50/50">
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
                  <User size={16} className="text-orange-500" /> Academic Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-50 p-4 rounded-2xl">
                    <span className="text-xs font-bold text-slate-400 block mb-1">Assigned Roll Number</span>
                    <span className="font-black text-slate-800 tracking-wide uppercase">{studentData.roll_number}</span>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-2xl">
                    <span className="text-xs font-bold text-slate-400 block mb-1">Enrolled Program Course</span>
                    <span className="font-black text-slate-800 flex items-center gap-2">
                      <BookOpen size={16} className="text-amber-500" /> {studentData.course_name}
                    </span>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-2xl md:col-span-2">
                    <span className="text-xs font-bold text-slate-400 block mb-1">Date of Admission</span>
                    <span className="font-bold text-slate-700 flex items-center gap-2">
                      <Calendar size={16} className="text-slate-400" /> 
                      {new Date(studentData.admission_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Row 2: Fee Architecture Financials */}
              <div className="bg-white p-8 rounded-[2rem] shadow-xl shadow-orange-900/5 border border-orange-50/50">
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
                  <CreditCard size={16} className="text-orange-500" /> Ledger Financial Overview
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="border border-slate-100 p-5 rounded-2xl text-center bg-slate-50/50">
                    <span className="text-xs font-black uppercase tracking-wider text-slate-400 block mb-1">Total Course Fee</span>
                    <span className="text-2xl font-black text-slate-900">₹{studentData.total_fee}</span>
                  </div>
                  
                  <div className="border border-emerald-100 p-5 rounded-2xl text-center bg-emerald-50/30">
                    <span className="text-xs font-black uppercase tracking-wider text-emerald-600 block mb-1">Paid Ledger Total</span>
                    <span className="text-2xl font-black text-emerald-600">₹{studentData.paid_fee}</span>
                  </div>

                  <div className={`p-5 rounded-2xl text-center border ${remainingFee > 0 ? 'bg-amber-50/40 border-amber-100' : 'bg-emerald-50/40 border-emerald-100'}`}>
                    <span className="text-xs font-black uppercase tracking-wider text-slate-400 block mb-1">Remaining Due Balance</span>
                    <span className={`text-2xl font-black ${remainingFee > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      ₹{remainingFee}
                    </span>
                  </div>
                </div>

                {remainingFee > 0 && (
                  <div className="mt-6 bg-orange-50/40 border border-orange-100/60 p-4 rounded-2xl text-xs font-medium text-orange-700 flex items-start gap-2.5">
                    <span className="inline-block px-1.5 py-0.5 bg-orange-500 text-white rounded text-[10px] font-black uppercase mt-0.5">Notice</span>
                    Please settle remaining pending balances at the institute branch counter before global examination unlock schedules.
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT 1 COL: Academic Certificates Output Block */}
            <div className="lg:col-span-1">
              <div className="bg-white p-8 rounded-[2rem] shadow-xl shadow-orange-900/5 border border-orange-50/50 h-full flex flex-col justify-between">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
                    <FileCheck size={16} className="text-orange-500" /> Issued Certifications
                  </h3>
                  <p className="text-slate-500 text-xs leading-relaxed mb-6">
                    Once your grading evaluation profiles achieve passing matrix records and account ledgers cleared, certificates sync automatically right here.
                  </p>
                </div>

                {studentData.certificate_url ? (
                  <div className="bg-orange-50/30 border border-orange-100 rounded-2xl p-5 text-center mt-auto">
                    <div className="h-12 w-12 bg-orange-100 rounded-xl text-orange-600 flex items-center justify-center mx-auto mb-4">
                      <FileCheck size={24} />
                    </div>
                    <h4 className="text-sm font-black text-slate-800">Course Certificate Ready</h4>
                    <p className="text-slate-400 text-[11px] font-medium mt-1 mb-4">Verified digital copy available.</p>
                    <a
                      href={studentData.certificate_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block w-full bg-slate-900 hover:bg-orange-600 text-white font-black py-3 rounded-xl text-xs transition-all shadow-md shadow-slate-100 hover:shadow-orange-200"
                    >
                      View & Download PDF
                    </a>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-100 rounded-2xl p-8 text-center mt-auto bg-slate-50/40">
                    <GraduationCap className="mx-auto text-slate-300 mb-3" size={32} />
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider">No Certificates Found</h4>
                    <p className="text-slate-400 text-[11px] mt-1">Pending graduation approval verification matrices.</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        )}
      </main>
    </div>
  );
}